/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { Add as AddIcon, DragHandle, RestartAlt } from '@mui/icons-material';
import {
  Button,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemText,
  styled,
  ListItemIcon,
} from '@mui/material';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import MultiSelect, {
  REASON_CLEAR,
  REASON_REMOVE_OPTION,
  REASON_SELECT_OPTION,
} from '@components/MultiSelect';
import { Typography } from '@components/styledComponents';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import { LoadingButton } from '@mui/lab';
import {
  editTabPriority,
  LOB_LIST_ROUTE,
} from '../../payment-options.breadcrumbs';

import {
  getLOBDetails,
  getLOBs,
  selectLOBList,
  updateMerchantTabPriority,
} from '../../store/payment-options-merchants';
import { getTabs, selectTabList } from '../../store/payment-options-tabs';

import SelectedMerchantsTable from '../../components/SelectedMerchantsTable';

const PriorityIndicator = styled('p')`
  margin: 0;

  &.new-priority {
    color: #0288d1;
  }
`;

export default function TabPriority() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permissions = usePermissions();

  // Edit-only page: the tab is hidden for view-only users, but the route is
  // still reachable by URL.
  useEffect(() => {
    if (!permissions?.canEditPaymentOptions) {
      navigate(LOB_LIST_ROUTE);
    }
  }, [navigate, permissions]);

  const lobList = useSelector(selectLOBList);
  const tabList = useSelector(selectTabList);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [selectedLOBMerchants, setSelectedLOBMerchants] = useState([]);
  const [showMerchantList, setShowMerchantList] = useState(false);
  const [showAddMethodBtn, setShowAddMethodBtn] = useState(false);
  const [addedMerchants, setAddedMerchants] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState({ show: false });

  const selectedMerchants = useRef({ lob: null, merchants: [] });
  const formValue = useRef({
    lob: null,
    tabs: [],
  });

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(editTabPriority));
  }, [dispatch]);

  useEffect(() => {
    setIsFetchingData(true);
    return Promise.all([dispatch(getLOBs()), dispatch(getTabs())]).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  useEffect(() => {
    formValue.current = { ...formValue.current, tabs: tabList };
  }, [tabList]);

  useEffect(
    () => setShowTable(addedMerchants && addedMerchants.length > 0),
    [addedMerchants],
  );

  const resetStep1 = useCallback(() => {
    selectedMerchants.current = { lob: null, merchants: [] };
    formValue.current = { ...formValue.current, lob: null, merchants: null };
    setShowMerchantList(false);
    setShowAddMethodBtn(false);
    setSelectedLOBMerchants([]);
  }, [selectedMerchants, formValue]);

  const handleAddMerchant = useCallback(() => {
    const newLobId = selectedMerchants.current.lob.lobId;
    const newMerchants = selectedMerchants.current.merchants;

    const existingIdx = addedMerchants.findIndex(
      (merchant) => merchant.lob.lobId === newLobId,
    );
    let updatedMerchants = addedMerchants.concat(selectedMerchants.current);

    if (existingIdx !== -1) {
      updatedMerchants = [...addedMerchants];
      updatedMerchants[existingIdx] = {
        ...selectedMerchants.current,
        merchants: newMerchants,
      };
    }
    setAddedMerchants(updatedMerchants);
    resetStep1();
  }, [selectedMerchants, addedMerchants, resetStep1]);

  const handleRemoveMerchant = useCallback((lobId, setValue) => {
    setAddedMerchants((current) => {
      const filteredCurrent = current.filter(({ lob }) => lob.lobId !== lobId);
      if (!filteredCurrent || filteredCurrent.length === 0) {
        setValue('lob', null);
        setValue('merchants', []);
      }
      return filteredCurrent;
    });
  }, []);

  const handleOnChangeLOB = useCallback(
    (value, reason) => {
      if (reason === REASON_CLEAR || value === null) {
        resetStep1();
        return;
      }

      if (reason === REASON_SELECT_OPTION || reason === REASON_REMOVE_OPTION) {
        setShowMerchantList(false);

        dispatch(getLOBDetails(value.lobId)).then(
          ({ payload: { merchantDetails } }) => {
            setSelectedLOBMerchants(merchantDetails);

            const lobExistsIdx = addedMerchants.findIndex(
              (merchant) => merchant.lob.lobId === value.lobId,
            );

            // reselect existing merchants
            selectedMerchants.current = {
              lob: value,
              merchants:
                lobExistsIdx > -1 ? addedMerchants[lobExistsIdx].merchants : [],
            };
            setShowMerchantList(true);
          },
        );
      }
    },
    [dispatch, resetStep1, selectedMerchants, addedMerchants],
  );

  const handleOnChangeMerchants = useCallback(
    (values, reason) => {
      if (reason === REASON_CLEAR) {
        setShowAddMethodBtn(false);
      }
      if (reason === REASON_SELECT_OPTION || reason === REASON_REMOVE_OPTION) {
        setShowAddMethodBtn(values && values.length > 0);
      }
      selectedMerchants.current = {
        ...selectedMerchants.current,
        merchants: values,
      };
    },
    [selectedMerchants],
  );

  const onSubmit = useCallback(
    (values) => {
      setFormSuccessMsg('');
      setFormErrorMsg('');
      setSubmitLoading(true);

      if (!values.tabs) {
        setSubmitLoading(false);
        return;
      }

      const merchantIds = addedMerchants.reduce((prev, { merchants }) => {
        const lobMerchants = merchants.map((merchant) => merchant.merchantId);
        return prev.concat(lobMerchants);
      }, []);

      const tabsPriority = values.tabs.reduce(
        (prev, tab, index) => ({ ...prev, [tab.id]: index + 1 }),
        {},
      );

      const payload = {
        merchantIds,
        tabsPriority,
      };

      if (!payload.merchantIds || !payload.merchantIds.length === 0) {
        setSubmitLoading(false);
        return;
      }
      if (!payload.tabsPriority || !payload.tabsPriority.length === 0) {
        setSubmitLoading(false);
        return;
      }

      dispatch(updateMerchantTabPriority(payload))
        .then((data) => {
          if (data.payload && data.payload.status === 'SUCCESS') {
            setFormSuccessMsg('Tab priority has been updated successfully');
          } else {
            setFormErrorMsg(
              data?.payload?.message ||
                data?.error?.message ||
                'Failed to update tab priority.',
            );
          }
        })
        .catch(() => {
          setFormErrorMsg('An error occurred while updating tab priority.');
        })
        .finally(() => {
          setSubmitLoading(false);
        });
    },
    [addedMerchants, dispatch],
  );

  const validate = useCallback(
    (values) => {
      const errors = {};
      if (!values.lob || addedMerchants.length === 0) {
        errors.lob = 'LOB is required';
      }
      if (!values.tabs || values.tabs.length === 0) {
        errors.tabs = 'Tabs are required';
      }
      return errors;
    },
    [addedMerchants],
  );

  const handleOnDragEnd = (fields) => (result) => {
    if (!result.destination) return;
    fields.move(result.source.index, result.destination.index);
  };

  const renderStep1 = (setValue) => (
    <>
      <Typography
        className="form-header"
        variant="formHeader">
        Step 1
      </Typography>
      {showTable && (
        <SelectedMerchantsTable
          merchants={addedMerchants}
          handleRemoveMerchant={(lobId) =>
            handleRemoveMerchant(lobId, setValue)
          }
        />
      )}
      <Grid
        container
        sx={{ marginTop: '0' }}
        spacing={2}>
        <Grid
          item
          xs={3}>
          <MultiSelect
            id="lobList"
            name="lob"
            items={lobList}
            value={selectedMerchants.current.lob}
            isOptionEqualToValue={(option, value) =>
              option.lobId === value.lobId
            }
            multiple={false}
            label="Select LOB"
            optionDisplayKey="lobName"
            onChange={handleOnChangeLOB}
          />
        </Grid>
        {showMerchantList && (
          <Grid
            item
            xs={9}
            md={5}>
            <MultiSelect
              id="merchantList"
              name="merchants"
              items={selectedLOBMerchants}
              value={selectedMerchants.current.merchants}
              isOptionEqualToValue={(option, value) =>
                option.merchantId === value.merchantId
              }
              label="Select platforms"
              optionDisplayKey="platformName"
              onChange={handleOnChangeMerchants}
            />
          </Grid>
        )}
        {showAddMethodBtn && (
          <Grid
            item
            xs={12}
            md={4}>
            <Button
              fullWidth
              size="large"
              startIcon={<RestartAlt />}
              variant="outlined"
              type="button"
              sx={{ marginBottom: '10px' }}
              onClick={resetStep1}>
              Reset
            </Button>
            <Button
              fullWidth
              size="large"
              startIcon={<AddIcon />}
              variant="contained"
              type="button"
              onClick={handleAddMerchant}>
              Add Merchant
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );

  const renderStep2 = () => (
    <>
      <Typography
        className="form-header"
        variant="formHeader">
        Step 2
      </Typography>
      <Typography
        variant="h4"
        sx={{ marginBottom: '16px' }}>
        Arrange Tabs
      </Typography>
      <Grid
        sx={{ maxHeight: '500px', overflow: 'auto' }}
        container
        spacing={2}>
        <Grid
          item
          xs={12}>
          <FieldArray name="tabs">
            {({ fields: { value: tabs }, fields }) => (
              <DragDropContext onDragEnd={handleOnDragEnd(fields)}>
                <Droppable droppableId="tabList">
                  {(provided) => (
                    <List
                      sx={{
                        width: '100%',
                        maxWidth: 360,
                        bgcolor: 'background.paper',
                      }}
                      {...provided.droppableProps}
                      ref={provided.innerRef}>
                      {tabs.map(({ id, name, defaultPriority }, index) => (
                        <Draggable
                          key={id}
                          draggableId={name}
                          index={index}>
                          {(innerProvided) => (
                            <ListItem
                              key={id}
                              ref={innerProvided.innerRef}
                              {...innerProvided.draggableProps}
                              {...innerProvided.dragHandleProps}>
                              <ListItemIcon>
                                <DragHandle />
                              </ListItemIcon>
                              <ListItemText
                                primary={name}
                                secondary={
                                  <PriorityIndicator
                                    className={
                                      defaultPriority !== index + 1 &&
                                      'new-priority'
                                    }>
                                    {defaultPriority === index + 1
                                      ? `Default Priority: ${defaultPriority}`
                                      : `New Priority: ${index + 1}`}
                                  </PriorityIndicator>
                                }
                              />
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </FieldArray>
        </Grid>
      </Grid>
    </>
  );

  const renderMessages = () => (
    <>
      {formSuccessMsg && (
        <Alert
          sx={{ marginTop: '32px' }}
          onClose={() => {
            setFormSuccessMsg('');
          }}
          severity="success">
          {formSuccessMsg}
        </Alert>
      )}
      {formErrorMsg && (
        <Alert
          sx={{ marginTop: '32px' }}
          onClose={() => {
            setFormErrorMsg('');
          }}
          severity="error">
          {formErrorMsg}
        </Alert>
      )}
    </>
  );

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="Update LOB Tab Priority"
      pageReady={!isFetchingData}>
      {!isFetchingData && (
        <Form
          onSubmit={onSubmit}
          initialValues={formValue.current}
          validate={validate}
          mutators={{
            ...arrayMutators,
            setValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
          render={({
            handleSubmit,
            valid,
            pristine,
            submitting,
            form: {
              mutators: { setValue },
            },
            form,
          }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              {renderStep1(setValue)}
              {renderStep2()}
              {renderMessages()}
              <LoadingButton
                id="submitBtn"
                style={{ marginTop: '32px' }}
                fullWidth
                size="large"
                variant="contained"
                type="submit"
                loading={submitLoading}
                disabled={
                  pristine ||
                  submitting ||
                  !valid ||
                  submitLoading ||
                  !permissions?.canEditPaymentOptions
                }>
                Submit
              </LoadingButton>
              <Button
                id="resetBtn"
                style={{ marginTop: '10px' }}
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => {
                  setFormSuccessMsg('');
                  setFormErrorMsg('');
                  form.reset();
                }}
                disabled={pristine || submitting || submitLoading}
                type="button">
                Reset
              </Button>
            </form>
          )}
        />
      )}
    </ApplicationPageTemplate>
  );
}
